import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { LpiCoverage } from './types';
import { useEchart } from './useEchart';
import ChartErrorState from '../cpiReports/ChartErrorState';
import { semanticColors, chartChrome } from '../../utils/chartColors';
import './LpiCoverageChart.scss';

interface LpiCoverageChartProps {
  data: LpiCoverage | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const LpiCoverageChart: FC<LpiCoverageChartProps> = ({
  data,
  isLoading,
  error,
  onRetry,
}) => {
  const { t } = useTranslation();

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (!data || data.total === 0) return null;

    const semantic = semanticColors();
    const chrome = chartChrome();

    return {
      aria: { enabled: true },
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => `${p.name}: ${p.value} (${p.percent}%)`,
      },
      legend: {
        orient: 'horizontal',
        bottom: '0%',
        left: 'center',
        textStyle: { color: chrome.text, fontSize: 13 },
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4, borderColor: chrome.surface, borderWidth: 2 },
          label: { show: false },
          data: [
            {
              name: String(t('lpi.chart.coverage.withLpi')),
              value: data.with_lpi,
              itemStyle: { color: semantic.good },
            },
            {
              name: String(t('lpi.chart.coverage.withoutLpi')),
              value: data.without_lpi,
              itemStyle: { color: semantic.bad },
            },
          ],
        },
      ],
    };
  }, [data, t]);

  const chartRef = useEchart(option);

  return (
    <div className="lpi-coverage-chart" data-testid="lpi-coverage-chart-card">
      <h3 className="lpi-coverage-chart__title">
        {t('lpi.chart.coverage.title')}
      </h3>

      {isLoading && (
        <div
          className="lpi-coverage-chart__skeleton"
          data-testid="lpi-coverage-chart-skeleton"
        />
      )}

      {error && !isLoading && <ChartErrorState onRetry={onRetry} />}

      {!error && !isLoading && data && data.total === 0 && (
        <p className="lpi-coverage-chart__empty">{t('lpi.chart.noData')}</p>
      )}

      {!error && !isLoading && option && (
        <div
          className="lpi-coverage-chart__canvas"
          data-testid="lpi-coverage-chart"
          ref={chartRef}
          aria-label={t('lpi.chart.coverage.title')}
        />
      )}
    </div>
  );
};

export default LpiCoverageChart;
